import { Alert, Space, Table, Tag, Typography } from "antd";
import type { OrderImportStatus } from "@/services/order-import.service";
import { ROUTES } from "@/constants/routes";

const { Text, Link } = Typography;

/**
 * Chiều cao vùng nhìn của bảng đơn = 5 dòng. Nhiều hơn thì antd tự sinh thanh
 * cuộn trong thân bảng và GIỮ nguyên hàng tiêu đề — quan trọng với file 500
 * dòng, nếu không modal dài vô tận và người dùng mất luôn tiêu đề cột khi cuộn.
 *
 * 5 dòng × 41px (chiều cao một dòng ở `size="small"`) = 205.
 */
const VISIBLE_ROWS = 5;
const ROW_HEIGHT_PX = 41;
const TABLE_SCROLL_Y = VISIBLE_ROWS * ROW_HEIGHT_PX;

const STATUS_TAG: Record<string, { color: string; label: string }> = {
  success: { color: "success", label: "Thành công" },
  warning: { color: "warning", label: "Thành công (chưa giữ tồn)" },
  failed: { color: "error", label: "Thất bại" },
};

/**
 * Bảng kết quả CẤP ĐƠN của một phiên nhập. Dùng chung cho modal tải lên
 * (Task 9) và drawer chi tiết ở trang lịch sử (Task 10) — đừng nhân bản sang
 * chỗ thứ hai.
 *
 * `status` của từng đơn: 'success' = tạo sạch; 'warning' = ĐÃ TẠO THÀNH CÔNG
 * nhưng chưa giữ được tồn (kho cho phép âm) — vẫn tính là thành công, không
 * hiện như lỗi; 'failed' = không tạo được đơn.
 *
 * `error_rows` là lỗi CẤP DÒNG không quy được về đơn nào (dòng hàng mồ côi,
 * phí ship âm...) — khác hẳn `order_results`, nên hiện ở khối riêng bên dưới
 * bảng đơn, không trộn chung.
 */
export interface OrderImportResultTableProps {
  job: OrderImportStatus;
  /**
   * Chiều cao vùng cuộn của bảng đơn. Bỏ trống ⇒ trần 5 dòng (dùng trong modal
   * tải lên, nơi bảng chỉ là một phần của màn hình). Drawer chi tiết phiên nhập
   * truyền chiều cao theo vùng nhìn để bảng chiếm trọn chỗ trống thay vì kẹt ở
   * 5 dòng giữa một khoảng trắng mênh mông.
   */
  scrollY?: number | string;
}

export function OrderImportResultTable({
  job,
  scrollY,
}: OrderImportResultTableProps) {
  // CHỈ "processing" mới hiện tiến độ: ở "pending" worker chưa nhặt job nên
  // `processed_orders/total_orders` đều là 0, hiện "Đang tạo đơn… 0/0" vừa vô
  // nghĩa vừa trùng với cảnh báo "Đang chờ hệ thống nhận xử lý…" của modal.
  const isRunning = job.status === "processing";

  return (
    <Space direction="vertical" size="middle" style={{ width: "100%" }}>
      {isRunning && (
        <Alert
          type="info"
          showIcon
          message={`Đang tạo đơn… ${job.processed_orders}/${job.total_orders}`}
        />
      )}

      <Table
        size="small"
        rowKey={(row) => String(row.order_index)}
        pagination={false}
        // Chỉ bật cuộn khi VƯỢT 5 dòng: đặt `scroll.y` vô điều kiện làm bảng ít
        // dòng vẫn chiếm trọn 205px và chừa khoảng trắng thừa dưới dòng cuối.
        // Caller truyền `scrollY` (drawer) ⇒ luôn cuộn theo chiều cao đó. Không
        // truyền (modal) ⇒ chỉ cuộn khi vượt 5 dòng, để bảng ít dòng không chừa
        // khoảng trắng thừa.
        scroll={
          scrollY !== undefined
            ? { y: scrollY }
            : (job.order_results ?? []).length > VISIBLE_ROWS
              ? { y: TABLE_SCROLL_Y }
              : undefined
        }
        dataSource={job.order_results ?? []}
        columns={[
          { title: "Dòng", dataIndex: "row", key: "row", width: 70 },
          { title: "Khách hàng", dataIndex: "customer_name", key: "customer_name" },
          { title: "SĐT", dataIndex: "customer_phone", key: "customer_phone", width: 120 },
          { title: "SL sản phẩm", dataIndex: "item_count", key: "item_count", width: 110 },
          {
            title: "Trạng thái",
            dataIndex: "status",
            key: "status",
            width: 180,
            render: (status: string) => {
              const tag = STATUS_TAG[status] ?? { color: "default", label: status };
              return <Tag color={tag.color}>{tag.label}</Tag>;
            },
          },
          {
            title: "Mã đơn / Lý do",
            key: "detail",
            render: (_: unknown, row) =>
              row.order_number ? (
                <Space direction="vertical" size={0}>
                  {/* Mở tab MỚI: người dùng đang giữa chừng xem kết quả của cả
                      file, điều hướng ngay trong tab hiện tại là mất bảng kết
                      quả (modal đóng, phiên nhập phải mở lại từ lịch sử). */}
                  {row.order_id ? (
                    <Link
                      href={ROUTES.orders.detail(row.order_id)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {row.order_number}
                    </Link>
                  ) : (
                    <Text>{row.order_number}</Text>
                  )}
                  {row.message ? <Text type="warning">{row.message}</Text> : null}
                </Space>
              ) : (
                <Text type="danger">{row.message}</Text>
              ),
          },
        ]}
      />

      {(job.error_rows ?? []).length > 0 && (
        <div>
          <Text strong>Dòng lỗi không thuộc đơn nào</Text>
          <Table
            size="small"
            rowKey={(row) => String(row.row)}
            pagination={false}
            dataSource={job.error_rows}
            columns={[
              { title: "Dòng", dataIndex: "row", key: "row", width: 70 },
              { title: "Lý do", dataIndex: "message", key: "message" },
            ]}
          />
        </div>
      )}
    </Space>
  );
}

export default OrderImportResultTable;
